import { LOCALES, SUPPORTED_LOCALES, type Locale } from '@recipes/contracts';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  cn,
} from '@recipes/ui';
import { Globe } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { currentLocale, isLocaleAvailable } from '@/lib/i18n';
import { chooseLocale, forgetPendingLocale, reconcileLocale } from '@/lib/i18n/language';
import { announce } from '@/components/feedback/live-region';
import { toast } from '@/components/feedback/toaster';

/** The languages this instance offers; the build's list until the configuration is known. */
export function useEnabledLocales(): Locale[] {
  const { data } = useConfig();
  const enabled = data?.locales?.map((l) => l.code).filter((code) => code in LOCALES);
  return enabled?.length ? enabled : [...SUPPORTED_LOCALES];
}

/**
 * Keeps the device and the profile on the same language (docs/partial/localization-spec.md 5.2):
 * after sign-in, when the profile changes on another device, and when the connection returns.
 * Rendered once by the app shell.
 */
export function useLanguageSync(): void {
  const { status, user, setUser } = useAuth();
  const userId = user?.id;
  const profileLocale = user?.locale;

  useEffect(() => {
    if (status !== 'authenticated' || !userId || !profileLocale) return;
    let cancelled = false;
    const run = () =>
      void reconcileLocale(
        profileLocale,
        async (locale) => (await api.users.updateProfile({ locale })).locale,
        navigator.onLine,
      ).then((saved) => {
        if (!cancelled && saved && user && saved !== user.locale)
          setUser({ ...user, locale: saved });
      });
    run();
    window.addEventListener('online', run);
    return () => {
      cancelled = true;
      window.removeEventListener('online', run);
    };
    // `user` is read for the merge only; re-running on every user object would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userId, profileLocale]);

  useEffect(() => {
    if (status === 'anonymous') forgetPendingLocale();
  }, [status]);
}

/**
 * The language button: a globe and, where there is room, the language in its own name.
 * Native names, no flags – languages are not countries. Switching happens in place.
 */
export function LanguageSwitcher({
  variant,
  className,
}: {
  /** `labelled`: sidebar and start pages. `icon`: the corner of a phone screen. */
  variant: 'labelled' | 'icon';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const { status, user, setUser } = useAuth();
  const locales = useEnabledLocales();
  const active = currentLocale();

  const select = async (locale: Locale) => {
    if (locale === active) return;
    if (!isLocaleAvailable(locale)) {
      toast.error(t('language.unavailableOffline'));
      return;
    }
    const signedIn = status === 'authenticated';
    try {
      await chooseLocale(locale, signedIn);
    } catch {
      toast.error(t('language.unavailableOffline'));
      return;
    }
    // The changed screen is the confirmation; screen readers hear it once, in the new language.
    announce(i18n.t('language.changed', { language: LOCALES[locale].nativeName }));
    if (!signedIn || !user) return;
    const saved = await reconcileLocale(
      user.locale,
      async (next) => (await api.users.updateProfile({ locale: next })).locale,
      navigator.onLine,
    );
    if (saved) setUser({ ...user, locale: saved });
  };

  if (locales.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('language.buttonLabel', { language: LOCALES[active].nativeName })}
        data-testid="language-switcher"
        className={cn(
          'inline-flex touch-target items-center justify-center gap-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          variant === 'labelled' ? 'px-3 py-2' : 'size-11 bg-background/80 backdrop-blur',
          className,
        )}
      >
        <Globe className="size-5" aria-hidden />
        {variant === 'labelled' && <span lang={active}>{LOCALES[active].nativeName}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('language.title')}</DropdownMenuLabel>
        {locales.map((code) => (
          <DropdownMenuCheckboxItem
            key={code}
            checked={code === active}
            onSelect={() => void select(code)}
            lang={code}
            data-testid={`language-option-${code}`}
          >
            {LOCALES[code].nativeName}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
